export const isGroupedStatsLeaf = (value: any) =>
    !!value &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    [
        'sum_of_plays',
        'sum_of_time',
        'impacts',
        'expected_plays_in_schedule',
        'expected_time_in_schedule',
        'expected_impacts_in_schedule',
    ].some(key => key in value);

export const pickGroupedWidgetPayload = (response: any) =>
    response?.data?.data ??
    response?.data ??
    response?.reportData ??
    response?.report_data ??
    response?.buckets ??
    response ??
    {};

export const OTHER_GROUP_LABEL = 'Other';

export const isBadGroupKey = (segment: unknown): boolean => {
    const trimmed = String(segment ?? '').trim();
    return trimmed === '' || /^["|]+$/.test(trimmed);
};

export const normalizeGroupKey = (segment: string): string =>
    isBadGroupKey(segment) ? OTHER_GROUP_LABEL : segment;

export const mergeGroupStats = (a: any, b: any): any => {
    if (!a) return { ...(b ?? {}) };
    if (!b) return a;
    const out: Record<string, any> = { ...a };
    Object.keys(b).forEach(key => {
        const bVal = (b as any)[key];
        if (typeof bVal === 'number' && Number.isFinite(bVal)) {
            const aVal = out[key];
            out[key] = (typeof aVal === 'number' && Number.isFinite(aVal) ? aVal : 0) + bVal;
        } else if (!(key in out)) {
            out[key] = bVal;
        }
    });
    return out;
};

const mergeGroupedNodes = (a: any, b: any): any => {
    if (isGroupedStatsLeaf(a) && isGroupedStatsLeaf(b)) return mergeGroupStats(a, b);
    if (
        a &&
        b &&
        typeof a === 'object' &&
        typeof b === 'object' &&
        !Array.isArray(a) &&
        !Array.isArray(b)
    ) {
        const out: Record<string, any> = { ...a };
        Object.entries(b).forEach(([key, value]) => {
            out[key] = key in out ? mergeGroupedNodes(out[key], value) : value;
        });
        return out;
    }
    return a;
};

export const foldGroupedResponseKeys = (node: any): any => {
    if (!node || typeof node !== 'object' || Array.isArray(node) || isGroupedStatsLeaf(node)) {
        return node;
    }
    const out: Record<string, any> = {};
    Object.entries(node).forEach(([key, child]) => {
        const normalizedKey = normalizeGroupKey(key);
        const foldedChild = foldGroupedResponseKeys(child);
        out[normalizedKey] =
            normalizedKey in out ? mergeGroupedNodes(out[normalizedKey], foldedChild) : foldedChild;
    });
    return out;
};

export const foldByFieldReportData = (reportData: Record<string, any>): Record<string, any> => {
    const out: Record<string, any> = {};
    Object.entries(reportData ?? {}).forEach(([key, value]) => {
        const normalizedKey = normalizeGroupKey(key);
        if (!(normalizedKey in out)) {
            out[normalizedKey] = Array.isArray(value) ? [...value] : value;
            return;
        }
        const existing = out[normalizedKey];
        if (Array.isArray(existing) && Array.isArray(value)) {
            const sumAt = (i: number) => (Number(existing[i]) || 0) + (Number(value[i]) || 0);
            const actual = sumAt(0);
            const expected = sumAt(1);
            const merged = [...existing];
            merged[0] = actual;
            merged[1] = expected;
            merged[2] = expected > 0 ? (actual / expected) * 100 : 0;
            merged[3] = sumAt(3);
            merged[4] = sumAt(4);
            merged[5] = sumAt(5);
            merged[6] = null;
            if (existing.length > 7 || value.length > 7) merged[7] = sumAt(7);
            out[normalizedKey] = merged;
        }
    });
    return out;
};

export const flattenGroupedStats = (
    value: any,
    path: string[] = [],
    foldBadKeysToOther = false,
): Array<{ label: string; path: string[]; stats: any }> => {
    const leaves: Array<{ path: string[]; stats: any }> = [];
    const walk = (node: any, currentPath: string[]) => {
        if (isGroupedStatsLeaf(node)) {
            if (currentPath.length > 0) leaves.push({ path: currentPath, stats: node });
            return;
        }
        if (!node || typeof node !== 'object' || Array.isArray(node)) return;
        Object.entries(node).forEach(([key, nested]) => walk(nested, [...currentPath, key]));
    };
    walk(value, path);

    if (!foldBadKeysToOther) {
        return leaves.map(({ path: leafPath, stats }) => ({
            label: leafPath.join(' - '),
            path: leafPath,
            stats,
        }));
    }

    const merged = new Map<string, { label: string; path: string[]; stats: any }>();
    leaves.forEach(({ path: leafPath, stats }) => {
        const normalizedSegments = leafPath.map(normalizeGroupKey);
        const normalizedPath = normalizedSegments.every(seg => seg === OTHER_GROUP_LABEL)
            ? [OTHER_GROUP_LABEL]
            : normalizedSegments;
        const label = normalizedPath.join(' - ');
        const existing = merged.get(label);
        if (existing) {
            existing.stats = mergeGroupStats(existing.stats, stats);
        } else {
            merged.set(label, { label, path: normalizedPath, stats: { ...stats } });
        }
    });
    return Array.from(merged.values());
};

export const getSummaryExportGroupColumns = (
    row: any,
    xAxisLabels: string[],
    dateLabelLabel: string,
) => {
    const label = row?.[dateLabelLabel];
    const exportGroupValues = row?.exportGroupValues;
    const groupValues =
        Array.isArray(exportGroupValues) && exportGroupValues.length
            ? exportGroupValues
            : String(label ?? '').split(' - ');

    return Object.fromEntries(
        xAxisLabels.map((xAxisLabel, index) => [
            xAxisLabel,
            groupValues[index] ?? (index === 0 ? label : ''),
        ]),
    );
};

export const buildStandardSummaryExportData = (
    rows: any[] | null | undefined,
    xAxisLabels: string[],
    dateLabelLabel: string,
) => {
    if (!Array.isArray(rows)) return undefined;

    return rows.map(row => {
        const groupColumns = getSummaryExportGroupColumns(row, xAxisLabels, dateLabelLabel);
        const {
            [dateLabelLabel]: _label,
            exportGroupValues: _exportGroupValues,
            ...rest
        } = row ?? {};

        return {
            ...groupColumns,
            ...rest,
        };
    });
};
